
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
    ArrowLeft,
    Save,
    Upload,
    Calendar,
    Loader2,
    X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { SimpleEditor } from '@/components/editor';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { blogStatusOptions, blogCategories } from '@/constants';
import { createBlogPost } from '@/services/blog';
import { uploadFile } from '@/services/upload';
import { encodeS3Url } from '@/components/encodeS3Url';

const formSchema = z.object({
    title: z.string().min(5, { message: "Title must be at least 5 characters" }),
    excerpt: z.string().min(10, { message: "Excerpt must be at least 10 characters" }),
    contentHTML: z.string().min(50, { message: "Content must be at least 50 characters" }),
    contentJSON: z.any({ required_error: "Editor content is required" }),
    category: z.string().min(1, { message: "Category is required" }),
    author: z.string().min(1, { message: "Author is required" }),
    authorRole: z.string().min(1, { message: "Author role is required" }),
    status: z.enum(['draft', 'published', 'scheduled']),
    publishDate: z.string().optional(),
    featuredImage: z.string().optional(),
    metaTitle: z.string().optional(),
    metaDescription: z.string().optional(),
});

const BlogCreate = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [tags, setTags] = useState<string[]>([]);
    const [newTag, setNewTag] = useState("");
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: '',
            excerpt: '',
            contentHTML: '',
            contentJSON: {},
            category: '',
            author: '',
            authorRole: '',
            status: 'draft',
            publishDate: new Date().toISOString().split('T')[0],
            featuredImage: '',
            metaTitle: '',
            metaDescription: '',
        },
    });

    const addTag = () => {
        if (newTag.trim() !== "" && !tags.includes(newTag.trim())) {
            setTags([...tags, newTag.trim()]);
            setNewTag("");
        }
    };

    const removeTag = (tagToRemove: string) => {
        setTags(tags.filter((tag) => tag !== tagToRemove));
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIsImageUploading(true);

            try {
                const imageUrl = await uploadFile(file);
                form.setValue('featuredImage', imageUrl);
                setImagePreview(imageUrl); // Set preview with the uploaded URL
                toast({
                    title: "Success",
                    description: "Image uploaded successfully",
                });
            } catch (error) {
                console.error("Error uploading image:", error);
                setImagePreview(null);
                form.setValue('featuredImage', '');
                toast({
                    title: "Error",
                    description: "Failed to upload image",
                    variant: "destructive"
                });
            } finally {
                setIsImageUploading(false);
            }
        }
    };

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        setIsLoading(true);
        try {
            const blogData = {
                ...values,
                tags,
            };

            // @ts-ignore - Ignoring type mismatch for now as service expects specific string literals
            await createBlogPost(blogData);

            toast({
                title: "Success",
                description: "Blog post created successfully.",
            });
            navigate('/admin/blog');
        } catch (error: any) {
            console.error("Error creating blog post:", error);
            toast({
                title: "Error",
                description: error.response?.data?.error || "Failed to create blog post.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />

            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="mb-8 flex items-center gap-4">
                        <Link to="/admin/blog">
                            <Button variant="outline" size="icon">
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        </Link>
                        <div>
                            <h1 className="text-3xl font-bold">Create New Blog Post</h1>
                            <p className="text-muted-foreground">
                                Create and publish a new blog article
                            </p>
                        </div>
                    </div>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-8">
                                    {/* Post Details */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Post Details</CardTitle>
                                            <CardDescription>
                                                Enter the basic details of your blog post
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-6">
                                            <FormField
                                                control={form.control}
                                                name="title"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Post Title</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="e.g. Top 10 Areas to Invest in Dubai" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="excerpt"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Excerpt</FormLabel>
                                                        <FormControl>
                                                            <Textarea
                                                                placeholder="A brief summary of your blog post"
                                                                className="min-h-[100px]"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <FormField
                                                    control={form.control}
                                                    name="category"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Category</FormLabel>
                                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select category" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    {blogCategories.map(option => (
                                                                        <SelectItem key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <FormField
                                                    control={form.control}
                                                    name="status"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Status</FormLabel>
                                                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select status" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    {blogStatusOptions.map(option => (
                                                                        <SelectItem key={option.value} value={option.value}>
                                                                            {option.label}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <FormField
                                                    control={form.control}
                                                    name="author"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Author Name</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="e.g. John Smith" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                <FormField
                                                    control={form.control}
                                                    name="authorRole"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Author Role</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="e.g. Real Estate Analyst" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* Content */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Post Content</CardTitle>
                                            <CardDescription>
                                                Write the full content of your blog post
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <FormField
                                                control={form.control}
                                                name="contentHTML"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Content</FormLabel>
                                                        <FormControl>
                                                            <SimpleEditor
                                                                content={field.value}
                                                                contentJSON={form.getValues('contentJSON')}
                                                                onChange={(html, json) => {
                                                                    field.onChange(html);
                                                                    form.setValue('contentJSON', json);
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Use the editor above to format your blog post content.
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </div>

                                <div className="space-y-8">
                                    {/* Publish Date */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Publishing</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <FormField
                                                control={form.control}
                                                name="publishDate"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Publish Date</FormLabel>
                                                        <div className="flex items-center gap-2">
                                                            <Calendar className="h-4 w-4 text-muted-foreground" />
                                                            <FormControl>
                                                                <Input type="date" {...field} />
                                                            </FormControl>
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                        <CardFooter>
                                            <Button type="submit" className="w-full" disabled={isLoading}>
                                                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                                Publish Post
                                            </Button>
                                        </CardFooter>
                                    </Card>

                                    {/* Featured Image */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Featured Image</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="border-2 border-dashed rounded-lg p-6 text-center">
                                                <div className="flex flex-col items-center">
                                                    {isImageUploading ? (
                                                        <div className="flex flex-col items-center justify-center py-4">
                                                            <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                                                            <p className="text-sm text-muted-foreground">Uploading image...</p>
                                                        </div>
                                                    ) : imagePreview ? (
                                                        <div className="relative w-full mb-4">
                                                            <img src={encodeS3Url(imagePreview)} alt="Preview" className="w-full h-48 object-cover rounded-md" />
                                                            <Button
                                                                type="button"
                                                                variant="destructive"
                                                                size="icon"
                                                                className="absolute top-2 right-2"
                                                                onClick={() => {
                                                                    setImagePreview(null);
                                                                    form.setValue('featuredImage', '');
                                                                }}
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <Upload className="h-8 w-8 text-muted-foreground mb-4" />
                                                            <p className="mb-2 text-sm font-semibold">
                                                                Drag & drop or click to upload
                                                            </p>
                                                        </>
                                                    )}
                                                    <Input
                                                        id="featuredImage"
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={handleImageUpload}
                                                    />
                                                    {!imagePreview && (
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => document.getElementById("featuredImage")?.click()}
                                                        >
                                                            Browse Files
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="featuredImage"
                                                render={({ field }) => (
                                                    <FormItem className="mt-4">
                                                        <FormLabel>Or Image URL</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="https://..." {...field} onChange={(e) => {
                                                                field.onChange(e);
                                                                setImagePreview(e.target.value);
                                                            }} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>

                                    {/* Tags */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Tags</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <div className="flex gap-2 mb-4">
                                                <Input
                                                    placeholder="Add a tag"
                                                    value={newTag}
                                                    onChange={(e) => setNewTag(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            e.preventDefault();
                                                            addTag();
                                                        }
                                                    }}
                                                />
                                                <Button type="button" onClick={addTag} size="sm">
                                                    Add
                                                </Button>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {tags.map((tag, index) => (
                                                    <Badge key={index} variant="secondary" className="px-3 py-1">
                                                        {tag}
                                                        <button
                                                            type="button"
                                                            className="ml-2 text-xs hover:text-destructive"
                                                            onClick={() => removeTag(tag)}
                                                        >
                                                            ×
                                                        </button>
                                                    </Badge>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>

                                    {/* SEO */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>SEO Settings</CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="metaTitle"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Meta Title</FormLabel>
                                                        <FormControl>
                                                            <Input placeholder="SEO Title" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="metaDescription"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Meta Description</FormLabel>
                                                        <FormControl>
                                                            <Textarea placeholder="SEO Description" className="min-h-[100px]" {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default BlogCreate;
